// Mobile-friendly modal wrapper. On mobile (≤768px), renders content as
// a bottom-anchored sheet that slides up from the bottom of the viewport
// — originating in the *easy zone* so confirming or dismissing doesn't
// require a hand-shift. On desktop, renders as a centered overlay
// (matching the app's existing modal pattern).
//
// Mobile features:
// - Drag handle (4×40px bar centered at top) makes the drag-down-to-dismiss
//   gesture discoverable.
// - Drag down by more than ~60px or release with downward velocity → close.
// - Backdrop tap closes.
// - Focus trap while open: Tab cycles within the sheet. On open, focus
//   moves to first focusable; on close, focus restores to whatever was
//   focused before the sheet opened.
// - visualViewport.resize listener scrolls the focused input into view
//   when the keyboard rises (otherwise textareas can sit under the
//   on-screen keyboard with no way to see what you're typing).
// - 85vh max height; content scrolls inside.
//
// Desktop features:
// - Existing center-overlay pattern (background scrim, click-outside-
//   to-close, max-width content). No drag handle.
//
// Usage:
//   <MobileBottomSheet open={showSettings} onClose={() => setShowSettings(false)}>
//     <h2>Settings</h2>
//     ...content...
//   </MobileBottomSheet>

import React, { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';

const DRAG_DISMISS_PX = 60;
const DRAG_VELOCITY_PX_PER_MS = 0.4;
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

const getFocusableElements = (container) => {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hasAttribute('disabled')) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    return true;
  });
};

export default function MobileBottomSheet({
  open,
  onClose,
  children,
  desktopMaxWidth = '500px',
  ariaLabel,
}) {
  const reduceMotion = useReducedMotion();
  const [mob, setMob] = useState(isMobileViewport);
  const [dragY, setDragY] = useState(0);
  const sheetRef = useRef(null);
  const previousFocusRef = useRef(null);
  const dragStartRef = useRef(null);

  // Keep mobile detection live in case the viewport changes while a sheet
  // is open (orientation change on tablet, devtools resize, etc.).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setMob(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Focus trap + restore. On open, snapshot the active element and shift
  // focus into the sheet; on close, restore to the previous focus.
  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    // Defer one frame so the sheet is rendered before we query focusables.
    const t = setTimeout(() => {
      const focusable = getFocusableElements(sheetRef.current)[0];
      (focusable || sheetRef.current)?.focus?.();
    }, 0);
    return () => {
      clearTimeout(t);
      previousFocusRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const focusable = getFocusableElements(sheet);
      if (focusable.length === 0) {
        e.preventDefault();
        sheet.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (!sheet.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Visual-viewport keyboard handling. When iOS / Android raise the soft
  // keyboard the layout viewport doesn't change, but visualViewport
  // shrinks. Scroll the focused input into view so the user can see what
  // they're typing instead of it sitting under the keyboard.
  useEffect(() => {
    if (!open || !mob || typeof window === 'undefined') return undefined;
    const scrollFocusedInputIntoView = () => {
      const active = document.activeElement;
      if (!active || !sheetRef.current?.contains(active)) return;
      if (active.scrollIntoView) active.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };
    const onFocusIn = (e) => {
      if (!sheetRef.current?.contains(e.target)) return;
      scrollFocusedInputIntoView();
    };
    document.addEventListener('focusin', onFocusIn);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', scrollFocusedInputIntoView);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      vv?.removeEventListener('resize', scrollFocusedInputIntoView);
    };
  }, [open, mob]);

  // Esc closes (desktop affordance; mobile uses backdrop or drag).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const startDrag = (clientY) => {
    dragStartRef.current = { y: clientY, t: performance.now() };
    setDragY(0);
  };
  const moveDrag = (clientY) => {
    if (!dragStartRef.current) return;
    const dy = clientY - dragStartRef.current.y;
    setDragY(Math.max(0, dy)); // never drag up
  };
  const endDrag = (clientY) => {
    if (!dragStartRef.current) return;
    const dy = clientY - dragStartRef.current.y;
    const dt = performance.now() - dragStartRef.current.t;
    const v = dt > 0 ? dy / dt : 0;
    dragStartRef.current = null;
    if (dy > DRAG_DISMISS_PX || v > DRAG_VELOCITY_PX_PER_MS) {
      setDragY(0);
      onClose?.();
    } else {
      setDragY(0);
    }
  };

  if (!open) return null;

  if (!mob) {
    // Desktop: existing centered-overlay modal pattern.
    return (
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
        }}
      >
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'var(--bg2, #1e1624)',
            border: '1px solid var(--bs, #2a2033)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: desktopMaxWidth,
            width: '100%',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  // Mobile: bottom sheet.
  return (
    <>
      {/* Scrim */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 999,
          opacity: 1 - Math.min(0.6, dragY / 400),
          transition: dragY > 0 ? 'none' : 'opacity 200ms ease-out',
        }}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: '85vh',
          background: 'var(--bg2, #1e1624)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          borderTop: '1px solid var(--bs, #2a2033)',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
          zIndex: 1000,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transform: `translateY(${dragY}px)`,
          transition: dragY > 0 || reduceMotion ? 'none' : 'transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        {/* Drag handle — onTouch handlers make it draggable; tappable area
            for grip is larger than the visible bar. */}
        <div
          onTouchStart={(e) => startDrag(e.touches[0].clientY)}
          onTouchMove={(e) => moveDrag(e.touches[0].clientY)}
          onTouchEnd={(e) => endDrag(e.changedTouches[0].clientY)}
          onTouchCancel={(e) => endDrag(e.changedTouches[0].clientY)}
          onMouseDown={(e) => startDrag(e.clientY)}
          style={{
            padding: '10px 0 6px',
            display: 'flex',
            justifyContent: 'center',
            cursor: 'grab',
            flexShrink: 0,
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '4px',
              borderRadius: '2px',
              background: 'var(--bs, #5c4a1f)',
              opacity: 0.6,
            }}
          />
        </div>
        <div style={{ padding: '4px 16px 16px', overflowY: 'auto', flex: 1, touchAction: 'pan-y' }}>
          {children}
        </div>
      </div>
    </>
  );
}
